import { FC, SVGProps, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, ChevronDownIcon, EnglishFlag, FrenchFlag } from "../../../assets/icons";
import { isCustomer } from "../../../guards/common/authentification-functions";
import { useSetPreferredLanguageMutation } from "../../../service/user-service";
import CircularProgressIndicator from "../circular-progress-indicator/CircularProgressIndicator";
import styles from "./LanguageSelector.module.scss";

interface SupportedLanguage {
	code: string;
	label: string;
	Flag: FC<SVGProps<SVGSVGElement>>;
}

// Backend already accepts any language code (see branding/translations endpoints) —
// adding a language here is enough to expose it in the picker once its content exists.
const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
	{ code: "en", label: "English", Flag: EnglishFlag },
	{ code: "fr", label: "Français", Flag: FrenchFlag },
];

/**
 * Lets the user pick the app language from SUPPORTED_LANGUAGES. Shows the
 * currently selected language's flag with a chevron, opening a menu of the
 * available options.
 *
 * Behaves differently depending on auth state:
 * - Logged out: switches the language locally (no server call).
 * - Logged in: calls setPreferredLanguage and only applies the new language
 *   once that call succeeds, showing a loader in place of the check icon on
 *   the pending row while it's in flight.
 */
const LanguageSelector: FC = () => {
	const { i18n } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [pendingCode, setPendingCode] = useState<string>();
	const [setPreferredLanguage] = useSetPreferredLanguageMutation();

	const current = SUPPORTED_LANGUAGES.find(language => language.code === i18n.language) ?? SUPPORTED_LANGUAGES[0];

	const selectLanguage = async (code: string) => {
		if (code === i18n.language) {
			setIsOpen(false);
			return;
		}

		if (!isCustomer()) {
			i18n.changeLanguage(code);
			setIsOpen(false);
			return;
		}

		setPendingCode(code);
		try {
			await setPreferredLanguage({ language: code }).unwrap();
			await i18n.changeLanguage(code);
		} catch {
			// Failed — leave the active language unchanged.
		}
		setPendingCode(undefined);
		setIsOpen(false);
	};

	const closeOnBlur = (event: React.FocusEvent<HTMLDivElement>) => {
		if (event.relatedTarget) return;
		setIsOpen(false);
	};

	return (
		<div className={styles.container} onBlur={closeOnBlur}>
			<button type="button" className={styles.trigger} onClick={() => setIsOpen(!isOpen)}>
				<current.Flag className={styles.flag} />
				<ChevronDownIcon className={styles.chevron} />
			</button>

			<div className={`${styles.dropdown} ${isOpen ? styles.show : ""}`}>
				{SUPPORTED_LANGUAGES.map(language => (
					<button
						type="button"
						key={language.code}
						className={styles.item}
						disabled={pendingCode !== undefined}
						onClick={() => selectLanguage(language.code)}
					>
						<language.Flag className={styles.flag} />
						<span className={styles.label}>{language.label}</span>
						{pendingCode === language.code && <CircularProgressIndicator height={16} width={16} />}
						{pendingCode === undefined && language.code === i18n.language && <CheckIcon className={styles.check} />}
					</button>
				))}
			</div>
		</div>
	);
};

export default LanguageSelector;
